import type { ElementType, FC, ReactNode } from 'react'

import { useRef } from 'react'
import { gsap, MOTION_QUERIES, SplitText, useGSAP } from '@/lib/motion'

interface SplitRevealProps {
    as?: ElementType
    children: ReactNode
    className?: string
    delay?: number
    stagger?: number
    start?: string
    id?: string
}

// Masked line reveal for static headings. Lines rise out of their own clipping
// mask the first time the heading enters the viewport. Never use this on CMS
// text: splitting rewrites the DOM that inline editing relies on.
const SplitReveal: FC<SplitRevealProps> = ({
    as: Tag = 'div',
    children,
    className,
    delay = 0,
    stagger = 0.09,
    start = 'top 88%',
    id
}): ReactNode => {
    const ref = useRef<HTMLElement>(null)

    useGSAP(() => {
        const element = ref.current

        if (!element) return

        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.motion, () => {
            const split = SplitText.create(element, {
                type: 'lines',
                mask: 'lines',
                autoSplit: true,
                onSplit: (self) => gsap.from(self.lines, {
                    yPercent: 108,
                    duration: 1.15,
                    ease: 'expo.out',
                    stagger,
                    delay,
                    scrollTrigger: {
                        trigger: element,
                        start,
                        once: true
                    }
                })
            })

            return () => split.revert()
        })

        return () => mm.revert()
    }, { scope: ref })

    return (
        <Tag ref={ref} id={id} className={className}>
            {children}
        </Tag>
    )
}

export default SplitReveal
